/**
 * Text content configuration for Climadiag Commune
 * Centralizes all UI texts, labels, messages and indicator content
 *
 * @file All text content used across the application
 */

// Import generated text data
import printTexts from '../../data/generated/print.json' with { type: 'json' };
import risksTexts from '../../data/generated/risks.json' with { type: 'json' };
import printLastPageTexts from '../../data/generated/print-last-page.json' with { type: 'json' };
import readingGuideTexts from '../../data/generated/reading-guide.json' with { type: 'json' };

// Import generated indicator data
import indicatorsTextsData from '../../data/generated/indicators-texts.json' with { type: 'json' };
import indicatorsMetadata from '../../data/texts/indicators/index.json' with { type: 'json' };

/**
 * Home page configuration
 * @type {Object}
 */
export const homePage = {
  title: 'Climadiag Commune',
  subtitle: 'À quoi ma commune\ndevra-t-elle s\'adapter ?',
  searchText: 'nom ou code (postal ou SIREN)',
  searchPlaceholder: 'nom ou code (postal ou SIREN)',
  printCoverSubtitle: 'Horizon {{horizon}} : dans une France à {{rwl}}',
  printCoverSubtitleOverseas: 'Horizon {{horizon}} : dans un territoire à {{rwl}}',

  // Additional modern configuration
  meta: {
    description: 'Découvrez les enjeux climatiques de votre commune avec Climadiag',
    keywords: ['climat', 'commune', 'adaptation', 'réchauffement climatique'],
  },

  // UI configuration
  ui: {
    showSearchOnLoad: true,
    enableAutocomplete: true,
    maxSearchResults: 10,
  }
};

/**
 * Entity characteristics texts
 * Labels for displaying entity properties
 * @type {Object}
 */
export const characteristicsText = {
  altMin: 'Altitude minimum',
  altMax: 'Altitude maximum',
  population: 'Population',
  habitants: 'habitants environ',
  risques: 'Risques naturels identifiés',
  autreEnjeu: 'Autre enjeu identifié',
  ilot: 'Sensibilité au phénomène d\'îlot de chaleur urbain'
};

/**
 * Territory-specific risk section headings
 * @type {Object<string, string>}
 */
const RISKS_HEADING_BY_DOMAIN = {
  'Nouvelle-Calédonie': 'Risques naturels identifiés par la Sécurité civile de la Nouvelle-Calédonie',
};

const RISKS_HEADING_DEFAULT = 'Risques naturels identifiés (source\u00A0: Géorisques)';

/**
 * Get the risk section heading for a given territory domain
 * @param {string|undefined} domaine - Entity domain
 * @returns {string} Risk heading text
 */
export const getRisksHeading = (domaine) =>
  (domaine && RISKS_HEADING_BY_DOMAIN[domaine]) || RISKS_HEADING_DEFAULT;

/**
 * Get the territory label to display after the entity name
 * @param {Object} entity - Entity data
 * @param {string} entity.domaine - Entity domain
 * @param {string} entity.nom_departement - Department name
 * @returns {string|null} Territory label or null (for SMSB)
 */
export const getTerritoryLabel = ({ domaine, nom_departement: nomDepartement } = {}) => {
  if (!domaine) return nomDepartement ?? null;
  if (domaine === 'Saint-Martin-Saint-Barthélemy') return null;
  if (domaine.startsWith('Polynésie-française')) return 'Polynésie française';
  return nomDepartement ?? null;
};

/**
 * Risk messages texts
 * Warning messages for different types of natural risks
 * @type {Object}
 */
/**
 * Risk messages texts
 * Warning messages for different types of natural risks
 *
 * Generated from Markdown sources during build process
 * Sources: front-next/data/texts/risks/*.md
 *
 * @type {Object}
 */
export const risksText = risksTexts;

/**
 * Chart legend configuration
 * Labels and descriptions for chart legend items
 * @type {Object}
 */
export const chartLegend = {
  linkToHowToReadText: 'Aller à « Comprendre les infographies »',
  legendItems: [
    {
      label: 'Valeur de référence',
      description: 'Période de référence {{ref_period}} (ou 1995-2014 pour le niveau de la mer)',
      type: 'ref'
    },
    {
      label: 'Valeur haute',
      description: 'Valeur haute des projections dans le climat auquel il faut se préparer d’ici {{horizon}}',
      type: 'max'
    },
    {
      label: 'Valeur médiane',
      description: 'Valeur médiane des projections dans le climat auquel il faut se préparer d’ici {{horizon}}',
      type: 'mean'
    },
    {
      label: 'Valeur basse',
      description: 'Valeur basse des projections dans le climat auquel il faut se préparer d’ici {{horizon}}',
      type: 'min'
    }
  ]
};

/**
 * Print configuration and tutorial messages
 * Instructions for PDF export per platform
 * @type {Object}
 */
export const print = printTexts;

/**
 * Last page content for print version
 * Additional resources and links displayed on the last page of PDF
 *
 * Generated from Markdown sources with YAML frontmatter during build process
 * Sources: front-next/data/texts/print-last-page/*.md
 *
 * @type {Array<{title: string, content: string, link: string, img: string}>}
 */
export const printLastPage = printLastPageTexts;

/**
 * Climate indicators text content
 * Complete list of texts per indicator (key, navigation, title, description)
 *
 * Generated from Markdown sources during build process
 * Sources: front-next/data/texts/indicators/<key>/<domain>.md
 *
 * Structure:
 * - key: Unique indicator identifier
 * - navigation: Short title for navigation menu
 * - title: Full display title
 * - description: Array of variant objects
 *   - domains: Array of domain identifiers this variant applies to
 *   - byOutlook: Array of 3 horizons (2030, 2050, 2100), each containing paragraphs
 *
 * @type {Array<{key: string, navigation: string, title: string, description: Array<{domains: string[], byOutlook: string[][]}>}>}
 */
export const indicatorsContent = Object.entries(indicatorsTextsData.indicators).map(([key, data]) => ({
  key,
  title: indicatorsMetadata.indicators[key].title,
  navigation: indicatorsMetadata.indicators[key].navigation,
  description: data.variants
}));

/**
 * Reading guide text content
 * Texts for different sections of the reading guide
 *
 * Generated from Markdown sources during build process
 * Sources: front-next/data/texts/reading-guide/<section>-<domain>.md
 *
 * Structure:
 * - sections: Object mapping section names to domain variants
 *   - Each section contains domain-specific text variants
 *   - 'default' variant is for France hexagonale
 *   - Other variants for specific domains (Réunion, Guyane, Mayotte, etc.)
 *
 * @type {Object<string, Object<string, string>>}
 */
export const readingGuide = readingGuideTexts.sections;

/**
 * Default export for compatibility
 */
export default {
  homePage,
  characteristicsText,
  risksText,
  chartLegend,
  print,
  printLastPage,
  indicatorsContent,
  readingGuide,
};
